// ============================================================================
// GAMIFICATION PARTAGÉE — composants et logique communs aux espaces personnels
//   (collaborateur ET responsable d'équipe). Extrait de pages/Pointage.js pour
//   que les deux espaces offrent EXACTEMENT la même expérience individuelle :
//   niveau + progression, série de jours (streak), badges, tendance, objectif,
//   célébration de passage de niveau et rappel « série en jeu ».
// ============================================================================
import { useState, useEffect, useCallback, useRef } from "react";
import { publicApi } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import {
  TrendingUp, TrendingDown, AlertTriangle, CalendarDays, CalendarRange, ChevronRight, Clock, Gauge,
  Trophy, Target, Award, Flame, Zap, Medal, BarChart3, Rocket, Star, Crown, Lock,
  Sparkles, Bell, X, Info,
} from "lucide-react";

const fmtH = (h) => (h === null || h === undefined ? "—" : Number(h).toFixed(2).replace(".", ","));

// Tons couleur des ZONES de ratio (fond + bordure + texte) — vert/orange/rouge.
const ZONE_TONE = {
  vert: { bg: "bg-[rgba(31,157,85,0.16)]", border: "border-[rgba(31,157,85,0.5)]", text: "text-emerald-700", label: "Objectif atteint" },
  orange: { bg: "bg-[rgba(245,158,11,0.20)]", border: "border-[rgba(245,158,11,0.55)]", text: "text-amber-600", label: "À améliorer" },
  rouge: { bg: "bg-[rgba(214,69,69,0.16)]", border: "border-[rgba(214,69,69,0.5)]", text: "text-rose-600", label: "Sous l'objectif" },
  none: { bg: "bg-[var(--surface-2)]", border: "border-[var(--border-soft)]", text: "text-muted-ink", label: "Pas de donnée" },
};
const zoneTone = (z) => ZONE_TONE[z] || ZONE_TONE.none;
const kindLabel = (k) => (k === "terrain" ? "Porte-à-porte" : "Site privé");
// Couleur de barre selon la zone du ratio (repli : orange de marque si non mesurable).
const ZONE_BAR = { vert: "bg-emerald-500", orange: "bg-amber-500", rouge: "bg-rose-500" };
const zoneBar = (z) => ZONE_BAR[z] || "bg-[var(--shariti-orange)]";

const MONTHS_FR = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];
const frMonth = (ym) => {
  if (!ym) return "—";
  const [y, m] = ym.split("-");
  return `${MONTHS_FR[Number(m) - 1] || m} ${y}`;
};

export const LEVEL_ICONS = [Flame, Zap, Award, Medal, Trophy, Trophy];
export const BADGE_ICONS = { rocket: Rocket, medal: Medal, award: Award, trophy: Trophy, flame: Flame, zap: Zap, star: Star, crown: Crown, target: Target };

// ---------------------------------------------------------------------------
// Hook : détection de PASSAGE DE NIVEAU + affichage du RAPPEL « série en jeu ».
//   Mémorise l'état par token en localStorage (1re visite silencieuse).
// ---------------------------------------------------------------------------
export function usePerfCelebrations(token, stats) {
  const [levelUp, setLevelUp] = useState(null);
  const [showReminder, setShowReminder] = useState(false);
  const [showRatioAlert, setShowRatioAlert] = useState(false);
  const [showRatioGreen, setShowRatioGreen] = useState(false);
  const [showRecap, setShowRecap] = useState(false);
  const levelCheckedRef = useRef(false);

  // PALIER NIVEAU : toute hausse d'index depuis la dernière visite → célébration.
  useEffect(() => {
    if (!stats || !stats.level || levelCheckedRef.current) return;
    levelCheckedRef.current = true;
    try {
      const key = `shariti_level_${token}`;
      const raw = localStorage.getItem(key);
      const idx = stats.level.index ?? 0;
      if (raw === null) {
        localStorage.setItem(key, String(idx));      // 1re visite : pas de célébration
      } else if (idx > Number(raw)) {
        localStorage.setItem(key, String(idx));
        setLevelUp(stats.level);                       // nouveau palier atteint
      } else if (idx < Number(raw)) {
        localStorage.setItem(key, String(idx));       // resync défensif
      }
    } catch (_) { /* localStorage indisponible */ }
  }, [stats, token]);

  // NOTIFICATION RAPPEL : une fois par jour (dismiss par date, réapparaît le lendemain).
  useEffect(() => {
    if (!stats) return;
    if (!stats.streak_at_risk) { setShowReminder(false); return; }
    try {
      const dismissed = localStorage.getItem(`shariti_streak_dismiss_${token}`);
      setShowReminder(dismissed !== (stats.today || ""));
    } catch (_) { setShowReminder(true); }
  }, [stats, token]);

  const dismissReminder = () => {
    try { localStorage.setItem(`shariti_streak_dismiss_${token}`, stats?.today || ""); } catch (_) { /* noop */ }
    setShowReminder(false);
  };
  const closeLevelUp = () => setLevelUp(null);

  // ALERTE ROUGE : rappel quand le ratio de la SEMAINE passe au rouge (une fois/semaine,
  // dismiss mémorisé par semaine ISO, réapparaît la semaine suivante).
  useEffect(() => {
    const wk = stats?.ratios?.semaine;
    if (!wk || wk.zone !== "rouge") { setShowRatioAlert(false); return; }
    try {
      const dismissed = localStorage.getItem(`shariti_ratio_dismiss_${token}`);
      setShowRatioAlert(dismissed !== (stats.ratios.week_start || ""));
    } catch (_) { setShowRatioAlert(true); }
  }, [stats, token]);

  const dismissRatioAlert = () => {
    try { localStorage.setItem(`shariti_ratio_dismiss_${token}`, stats?.ratios?.week_start || ""); } catch (_) { /* noop */ }
    setShowRatioAlert(false);
  };

  // ALERTE VERTE : féliciter quand le ratio de la semaine REPASSE au vert (transition
  // depuis rouge/orange). Détectée via la dernière zone vue (localStorage), une fois/semaine.
  useEffect(() => {
    const wk = stats?.ratios?.semaine;
    const ws = stats?.ratios?.week_start;
    if (!wk || !ws) return;
    try {
      const key = `shariti_ratio_zone_${token}`;
      const raw = localStorage.getItem(key);
      let prev = null;
      if (raw) { const [rws, rz] = raw.split("|"); if (rws === ws) prev = rz; }
      if (wk.zone === "vert" && (prev === "rouge" || prev === "orange")) {
        if (localStorage.getItem(`shariti_ratio_green_${token}`) !== ws) setShowRatioGreen(true);
      }
      localStorage.setItem(key, `${ws}|${wk.zone || "na"}`);
    } catch (_) { /* noop */ }
  }, [stats, token]);

  const dismissRatioGreen = () => {
    try { localStorage.setItem(`shariti_ratio_green_${token}`, stats?.ratios?.week_start || ""); } catch (_) { /* noop */ }
    setShowRatioGreen(false);
  };

  // RÉCAP HEBDO : chaque lundi, un petit récap de la semaine passée (dismiss par semaine).
  useEffect(() => {
    const rc = stats?.recap_semaine;
    if (!rc || !rc.due) { setShowRecap(false); return; }
    try {
      const dismissed = localStorage.getItem(`shariti_recap_dismiss_${token}`);
      setShowRecap(dismissed !== (rc.week_start || ""));
    } catch (_) { setShowRecap(true); }
  }, [stats, token]);

  const dismissRecap = () => {
    try { localStorage.setItem(`shariti_recap_dismiss_${token}`, stats?.recap_semaine?.week_start || ""); } catch (_) { /* noop */ }
    setShowRecap(false);
  };

  return {
    levelUp, closeLevelUp, showReminder, dismissReminder,
    showRatioAlert, dismissRatioAlert, showRatioGreen, dismissRatioGreen,
    showRecap, dismissRecap,
  };
}

// Bandeau gamifié TOUJOURS visible (au-dessus des onglets) : met la performance en avant
// pour attirer l'œil sans avoir à ouvrir « Ma perf ». Cliquable → ouvre l'onglet détaillé.
export function PerfStrip({ stats, onOpen }) {
  if (!stats) return null;
  const lvl = stats.level || {};
  const LvIcon = LEVEL_ICONS[lvl.index || 0] || Trophy;
  const objPct = Math.min(100, stats.objectif_pct || 0);
  return (
    <button type="button" onClick={onOpen} data-testid="perf-strip"
      className="relative mb-4 flex w-full items-center gap-3 overflow-hidden rounded-2xl bg-gradient-to-br from-[#EE722E] to-[#F4A261] p-3 text-left text-white shadow-[var(--shadow-md)] transition-transform duration-150 active:scale-[0.99]">
      <span className="pointer-events-none absolute inset-y-0 left-0 w-1/3 -translate-x-full bg-gradient-to-r from-transparent via-white/30 to-transparent"
        style={{ animation: "shimmer 3s ease-in-out infinite" }} aria-hidden="true" />
      <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-white/20">
        <LvIcon className="h-6 w-6" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="truncate text-[13px] font-extrabold leading-tight" data-testid="perf-strip-level">{lvl.name || "—"}</span>
          {stats.current_streak > 0 && (
            <span className="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-white/20 px-1.5 py-0.5 text-[10px] font-bold" data-testid="perf-strip-streak">
              <Flame className="h-3 w-3" />{stats.current_streak} j
            </span>
          )}
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-[11px] text-white/90">
          <span><b className="text-white tabular-nums">{stats.bulletins_mois ?? 0}</b> bulletins ce mois</span>
          <span className="opacity-60">•</span>
          <span className="tabular-nums">{stats.badges_unlocked ?? 0}/{stats.badges_total ?? 0} badges</span>
        </div>
        <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-white/25">
          <div className="h-full rounded-full bg-white transition-[width] duration-500" style={{ width: `${objPct}%` }} />
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-0.5 text-[11px] font-extrabold uppercase tracking-wide">
        Ma perf <ChevronRight className="h-4 w-4" />
      </div>
    </button>
  );
}

// Célébration animée au déblocage d'un NOUVEAU badge (pop + confettis). Défile les
// badges un par un si plusieurs sont débloqués d'un coup. Ferme au clic sur le fond.
export function BadgeUnlockCelebration({ badges, onClose }) {
  const [idx, setIdx] = useState(0);
  if (!badges || badges.length === 0) return null;
  const b = badges[Math.min(idx, badges.length - 1)];
  const Icon = BADGE_ICONS[b.icon] || Award;
  const isLast = idx >= badges.length - 1;
  const colors = ["#EE722E", "#F4A261", "#FCDB53", "#5D3FD3", "#1F9D55"];
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-[rgba(24,24,24,0.55)] p-6 backdrop-blur-sm animate-badge-overlay-in"
      role="dialog" aria-modal="true" data-testid="badge-celebration" onClick={onClose}>
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
        {Array.from({ length: 26 }).map((_, i) => {
          const left = Math.round((i / 26) * 100);
          const dx = Math.round(Math.random() * 160 - 80);
          const rot = Math.round(Math.random() * 540 + 180);
          const delay = Math.round(Math.random() * 350);
          const size = 6 + Math.round(Math.random() * 6);
          return (
            <span key={i} className="confetti-piece absolute top-[26%] rounded-[2px]"
              style={{ left: `${left}%`, width: size, height: size, background: colors[i % colors.length],
                "--dx": `${dx}px`, "--rot": `${rot}deg`, animationDelay: `${delay}ms` }} />
          );
        })}
      </div>

      <div className="relative w-full max-w-[300px] rounded-3xl border border-[var(--border-soft)] bg-white p-6 text-center shadow-[var(--shadow-md)] animate-badge-card-in"
        onClick={(e) => e.stopPropagation()} data-testid="badge-celebration-card">
        <div className="text-[11px] font-extrabold uppercase tracking-[0.18em] text-[var(--shariti-orange)]">Nouveau badge débloqué</div>
        <div className="relative mx-auto mt-4 grid h-24 w-24 place-items-center">
          <span className="absolute inset-0 rounded-full bg-[rgba(238,114,46,0.25)] animate-badge-ring" aria-hidden="true" />
          <div className="animate-badge-pop grid h-24 w-24 place-items-center rounded-full bg-gradient-to-br from-[#EE722E] to-[#F4A261] text-white shadow-[var(--shadow-md)]">
            <Icon className="h-11 w-11" />
          </div>
        </div>
        <div className="mt-4 text-xl font-extrabold text-ink" data-testid="badge-celebration-label">{b.label}</div>
        <div className="mt-1 text-[13px] text-muted-ink">{b.desc}</div>
        {badges.length > 1 && (
          <div className="mt-3 text-[11px] font-bold text-muted-ink" data-testid="badge-celebration-progress">{Math.min(idx + 1, badges.length)} / {badges.length}</div>
        )}
        <button
          onClick={() => (isLast ? onClose() : setIdx((v) => v + 1))}
          data-testid="badge-celebration-next"
          className="mt-5 w-full rounded-xl bg-[var(--shariti-orange)] px-4 py-3 text-sm font-extrabold text-white transition-transform duration-150 hover:scale-[1.02] active:scale-95">
          {isLast ? "Super !" : "Badge suivant"}
        </button>
      </div>
    </div>
  );
}

// Célébration animée de PASSAGE DE NIVEAU (pop de l'icône du niveau + confettis).
// Distincte des badges : carte en dégradé orange, grand niveau + prochain palier.
export function LevelUpCelebration({ level, onClose }) {
  if (!level) return null;
  const Icon = LEVEL_ICONS[level.index || 0] || Trophy;
  const colors = ["#FFFFFF", "#FCDB53", "#FFD9B8", "#5D3FD3", "#1F9D55"];
  return (
    <div className="fixed inset-0 z-[210] flex items-center justify-center bg-[rgba(24,24,24,0.6)] p-6 backdrop-blur-sm animate-badge-overlay-in"
      role="dialog" aria-modal="true" data-testid="levelup-celebration" onClick={onClose}>
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
        {Array.from({ length: 30 }).map((_, i) => {
          const left = Math.round((i / 30) * 100);
          const dx = Math.round(Math.random() * 180 - 90);
          const rot = Math.round(Math.random() * 540 + 180);
          const delay = Math.round(Math.random() * 350);
          const size = 6 + Math.round(Math.random() * 7);
          return (
            <span key={i} className="confetti-piece absolute top-[24%] rounded-[2px]"
              style={{ left: `${left}%`, width: size, height: size, background: colors[i % colors.length],
                "--dx": `${dx}px`, "--rot": `${rot}deg`, animationDelay: `${delay}ms` }} />
          );
        })}
      </div>

      <div className="relative w-full max-w-[320px] overflow-hidden rounded-3xl border border-white/20 bg-gradient-to-br from-[#EE722E] to-[#F4A261] p-7 text-center text-white shadow-[var(--shadow-md)] animate-badge-card-in"
        onClick={(e) => e.stopPropagation()} data-testid="levelup-celebration-card">
        <div className="flex items-center justify-center gap-1.5 text-[11px] font-extrabold uppercase tracking-[0.18em] text-white/90">
          <Sparkles className="h-3.5 w-3.5" /> Niveau supérieur
        </div>
        <div className="relative mx-auto mt-4 grid h-28 w-28 place-items-center">
          <span className="absolute inset-0 rounded-full bg-white/25 animate-badge-ring" aria-hidden="true" />
          <div className="animate-badge-pop grid h-28 w-28 place-items-center rounded-full bg-white/20 ring-4 ring-white/40">
            <Icon className="h-14 w-14" />
          </div>
        </div>
        <div className="mt-5 text-[13px] font-bold uppercase tracking-wide text-white/85" data-testid="levelup-celebration-index">
          Niveau {(level.index ?? 0) + 1} / {level.total_levels || 6}
        </div>
        <div className="mt-0.5 text-2xl font-extrabold leading-tight" data-testid="levelup-celebration-name">{level.name}</div>
        <div className="mt-2 text-[13px] leading-snug text-white/90" data-testid="levelup-celebration-next">
          {level.next_name
            ? `Prochain palier : ${level.next_name} — encore ${level.to_next} bulletin${level.to_next > 1 ? "s" : ""}.`
            : "Vous avez atteint le sommet — vous êtes une Légende !"}
        </div>
        <button onClick={onClose} data-testid="levelup-celebration-close"
          className="mt-6 w-full rounded-xl bg-white px-4 py-3 text-sm font-extrabold text-[var(--shariti-orange)] transition-transform duration-150 hover:scale-[1.02] active:scale-95">
          Continuer
        </button>
      </div>
    </div>
  );
}

// Bannière de RAPPEL « série en jeu » — s'affiche quand une série est en cours mais
// qu'aucun bulletin n'a encore été compté aujourd'hui. Refermable (une fois par jour).
export function StreakReminderBanner({ streak, onDismiss }) {
  return (
    <div className="relative mb-4 flex items-start gap-3 rounded-2xl border border-[rgba(217,119,6,0.35)] bg-[rgba(245,158,11,0.10)] p-3.5 shadow-[var(--shadow-sm)] animate-badge-card-in"
      data-testid="streak-reminder">
      <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[rgba(245,158,11,0.18)]">
        <Flame className="h-5 w-5 text-[#D97706]" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 text-[13px] font-extrabold text-[#B45309]" data-testid="streak-reminder-title">
          <Bell className="h-3.5 w-3.5" /> Votre série est en jeu !
        </div>
        <p className="mt-0.5 text-[12px] leading-snug text-ink" data-testid="streak-reminder-message">
          Vous êtes sur <b className="tabular-nums">{streak} jour{streak > 1 ? "s" : ""} d'affilée</b>.
          {" "}Faites au moins <b>1 bulletin aujourd'hui</b> pour ne pas la perdre.
        </p>
      </div>
      <button onClick={onDismiss} data-testid="streak-reminder-dismiss" aria-label="Masquer le rappel"
        className="shrink-0 rounded-lg p-1 text-muted-ink transition-colors hover:bg-black/5">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

// Bannière d'ALERTE ROUGE — s'affiche quand le ratio BS/h de la SEMAINE est dans le rouge.
// Refermable (une fois par semaine). Motive à repasser au vert.
export function RatioAlertBanner({ ratios, onDismiss }) {
  const wk = ratios?.semaine || {};
  const s = (ratios?.seuils || {})[wk.kind === "terrain" ? "terrain" : "magasin"] || {};
  return (
    <div className="relative mb-4 flex items-start gap-3 rounded-2xl border border-[rgba(214,69,69,0.4)] bg-[rgba(214,69,69,0.10)] p-3.5 shadow-[var(--shadow-sm)] animate-badge-card-in"
      data-testid="ratio-alert">
      <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[rgba(214,69,69,0.18)]">
        <TrendingDown className="h-5 w-5 text-rose-600" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 text-[13px] font-extrabold text-rose-700" data-testid="ratio-alert-title">
          <AlertTriangle className="h-3.5 w-3.5" /> Ratio de la semaine dans le rouge
        </div>
        <p className="mt-0.5 text-[12px] leading-snug text-ink" data-testid="ratio-alert-message">
          Votre productivité {kindLabel(wk.kind).toLowerCase()} est à <b className="tabular-nums text-rose-700">{wk.bs_h == null ? "—" : fmtH(wk.bs_h)} BS/h</b>
          {s.vert ? <> — objectif <b className="tabular-nums">{fmtH(s.vert)}</b> (min. <b className="tabular-nums">{fmtH(s.orange)}</b>)</> : null}.
          {" "}Boostez vos bulletins pour repasser au vert !
        </p>
      </div>
      <button onClick={onDismiss} data-testid="ratio-alert-dismiss" aria-label="Masquer l'alerte"
        className="shrink-0 rounded-lg p-1 text-muted-ink transition-colors hover:bg-black/5">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

// Bannière d'ALERTE VERTE — félicite quand le ratio de la semaine repasse au vert.
export function RatioGreenBanner({ ratios, onDismiss }) {
  const wk = ratios?.semaine || {};
  return (
    <div className="relative mb-4 flex items-start gap-3 rounded-2xl border border-[rgba(31,157,85,0.4)] bg-[rgba(31,157,85,0.10)] p-3.5 shadow-[var(--shadow-sm)] animate-badge-card-in"
      data-testid="ratio-green">
      <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[rgba(31,157,85,0.18)]">
        <Sparkles className="h-5 w-5 text-emerald-600" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 text-[13px] font-extrabold text-emerald-700" data-testid="ratio-green-title">
          <TrendingUp className="h-3.5 w-3.5" /> Bravo, vous repassez au vert !
        </div>
        <p className="mt-0.5 text-[12px] leading-snug text-ink" data-testid="ratio-green-message">
          Votre productivité {kindLabel(wk.kind).toLowerCase()} de la semaine est à
          {" "}<b className="tabular-nums text-emerald-700">{wk.bs_h == null ? "—" : fmtH(wk.bs_h)} BS/h</b> — objectif atteint. Continuez comme ça !
        </p>
      </div>
      <button onClick={onDismiss} data-testid="ratio-green-dismiss" aria-label="Masquer"
        className="shrink-0 rounded-lg p-1 text-muted-ink transition-colors hover:bg-black/5">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

// Bannière RÉCAP HEBDO — affichée le lundi : bilan de la semaine passée (bulletins + zone).
export function WeeklyRecapBanner({ recap, onDismiss }) {
  const t = zoneTone(recap?.zone);
  return (
    <div className="relative mb-4 flex items-start gap-3 rounded-2xl border border-[rgba(238,114,46,0.3)] bg-[rgba(238,114,46,0.06)] p-3.5 shadow-[var(--shadow-sm)] animate-badge-card-in"
      data-testid="recap-banner">
      <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[rgba(238,114,46,0.14)]">
        <CalendarRange className="h-5 w-5 text-[var(--shariti-orange)]" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[13px] font-extrabold text-ink" data-testid="recap-title">Récap de la semaine passée</div>
        <p className="mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[12px] leading-snug text-ink" data-testid="recap-message">
          <b className="tabular-nums text-[var(--shariti-orange)]">{recap?.bulletins || 0}</b> bulletin{(recap?.bulletins || 0) > 1 ? "s" : ""}
          {recap?.bs_h != null && (
            <span>· <b className="tabular-nums">{fmtH(recap.bs_h)}</b> BS/h ({kindLabel(recap.kind)})</span>
          )}
          {recap?.zone && (
            <span className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] font-bold ${t.bg} ${t.border} ${t.text}`}>
              <span className={`h-2 w-2 rounded-sm ${zoneBar(recap.zone)}`} />{t.label}
            </span>
          )}
        </p>
      </div>
      <button onClick={onDismiss} data-testid="recap-dismiss" aria-label="Masquer le récap"
        className="shrink-0 rounded-lg p-1 text-muted-ink transition-colors hover:bg-black/5">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

// ============================================================================
// TABLEAU DE BORD PERSONNEL — gamification (niveaux, objectif, records, tendance).
//   Salariés : bulletins + heures + ratio BS/h. Indépendants : bulletins seuls.
//   `statsUrl` permet de réutiliser le composant pour le responsable (endpoint /resp).
// ============================================================================
export function CollabDashboard({ token, preloaded, statsUrl }) {
  const [data, setData] = useState(preloaded || null);
  const [loading, setLoading] = useState(!preloaded);
  const [newBadges, setNewBadges] = useState([]);   // badges nouvellement débloqués (célébration)
  const [freshIds, setFreshIds] = useState([]);      // ids à marquer « Nouveau » sur la grille
  const celebratedRef = useRef(false);
  const url = statsUrl || `/pointage/public/${token}/mes-stats`;

  // Détection des NOUVEAUX badges vs la dernière visite (mémorisée en localStorage).
  const runBadgeDiff = useCallback((payload) => {
    if (celebratedRef.current || !payload) return;
    try {
      const key = `shariti_badges_${token}`;
      const seenRaw = localStorage.getItem(key);
      const seen = seenRaw ? JSON.parse(seenRaw) : null;
      const unlockedIds = (payload.badges || []).filter((x) => x.unlocked).map((x) => x.id);
      if (seen === null) {
        // 1re visite : on enregistre l'état SANS célébrer les badges déjà acquis.
        localStorage.setItem(key, JSON.stringify(unlockedIds));
      } else {
        const fresh = (payload.badges || []).filter((x) => x.unlocked && !seen.includes(x.id));
        localStorage.setItem(key, JSON.stringify(unlockedIds));
        if (fresh.length > 0) { setFreshIds(fresh.map((x) => x.id)); setNewBadges(fresh); }
      }
      celebratedRef.current = true;
    } catch (_) { /* localStorage indisponible : pas de célébration */ }
  }, [token]);

  useEffect(() => {
    let alive = true;
    // Si les stats sont déjà chargées par le shell, on les réutilise (pas de 2e appel).
    if (preloaded) {
      setData(preloaded);
      setLoading(false);
      runBadgeDiff(preloaded);
      return () => { alive = false; };
    }
    (async () => {
      setLoading(true);
      try {
        const r = await publicApi.get(url);
        if (!alive) return;
        setData(r.data);
        runBadgeDiff(r.data);
      } catch (e) {
        if (alive) { setData(null); toast.error(e?.response?.data?.detail || "Statistiques indisponibles."); }
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [url, preloaded, runBadgeDiff]);

  if (loading) return <div className="space-y-3" data-testid="collab-dash-loading"><Skeleton className="h-36 w-full rounded-2xl" /><Skeleton className="h-28 w-full rounded-2xl" /><Skeleton className="h-40 w-full rounded-2xl" /></div>;
  if (!data) return <div className="rounded-2xl border border-dashed border-[var(--border-soft)] bg-[var(--surface-2)] p-8 text-center text-sm text-muted-ink" data-testid="collab-dash-empty">Statistiques indisponibles pour le moment.</div>;

  const lvl = data.level || {};
  const LvIcon = LEVEL_ICONS[lvl.index || 0] || Trophy;
  const maxWeek = Math.max(1, ...((data.weeks || []).map((w) => w.n)));
  const objPct = Math.min(100, data.objectif_pct || 0);

  return (
    <div className="space-y-4" data-testid="collab-dashboard">
      {newBadges.length > 0 && (
        <BadgeUnlockCelebration badges={newBadges} onClose={() => setNewBadges([])} />
      )}
      {/* HÉRO — niveau + progression (dégradé réservé au bloc héro) */}
      <div className="relative overflow-hidden rounded-2xl border border-[var(--border-soft)] bg-gradient-to-br from-[#EE722E] to-[#F4A261] p-4 text-white shadow-[var(--shadow-md)]" data-testid="collab-dash-level">
        <div className="flex items-center gap-3">
          <div className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-white/20">
            <LvIcon className="h-6 w-6" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[11px] font-bold uppercase tracking-wide text-white/80">Niveau {(lvl.index ?? 0) + 1}/{lvl.total_levels || 6}</div>
            <div className="text-xl font-extrabold leading-tight" data-testid="collab-dash-level-name">{lvl.name || "—"}</div>
          </div>
          <div className="text-right">
            <div className="text-2xl font-extrabold tabular-nums" data-testid="collab-dash-total">{(data.bulletins_total || 0).toLocaleString("fr-FR")}</div>
            <div className="text-[10px] font-semibold uppercase tracking-wide text-white/80">bulletins au total</div>
          </div>
        </div>
        {lvl.next_name ? (
          <div className="mt-3">
            <div className="mb-1 flex items-center justify-between text-[11px] font-semibold text-white/90">
              <span>Vers {lvl.next_name}</span>
              <span>Encore {lvl.to_next}</span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-white/25">
              <div className="h-full rounded-full bg-white transition-[width] duration-500" style={{ width: `${lvl.pct_to_next || 0}%` }} />
            </div>
          </div>
        ) : (
          <div className="mt-3 text-[12px] font-semibold text-white/90">Niveau maximum atteint — vous êtes une Légende !</div>
        )}
      </div>

      {/* SÉRIE DE JOURS (streak) — motivation quotidienne */}
      <div className={`flex items-center gap-3 rounded-2xl border p-4 shadow-[var(--shadow-sm)] ${data.streak_at_risk ? "border-[rgba(217,119,6,0.35)] bg-[rgba(245,158,11,0.08)]" : "border-[var(--border-soft)] bg-white"}`} data-testid="collab-dash-streak" data-at-risk={!!data.streak_at_risk}>
        <div className={`grid h-14 w-14 shrink-0 place-items-center rounded-2xl ${data.current_streak > 0 ? (data.streak_at_risk ? "bg-[rgba(245,158,11,0.18)]" : "bg-[rgba(238,114,46,0.12)]") : "bg-[var(--surface-2)]"}`}>
          <Flame className={`h-7 w-7 ${data.current_streak > 0 ? (data.streak_at_risk ? "text-[#D97706]" : "text-[var(--shariti-orange)]") : "text-muted-ink"}`} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-muted-ink">
            Série en cours
            {data.streak_at_risk && (
              <span className="inline-flex items-center gap-0.5 rounded-full bg-[rgba(245,158,11,0.2)] px-1.5 py-0.5 text-[9px] font-extrabold tracking-normal text-[#B45309]" data-testid="collab-dash-streak-risk">
                <Bell className="h-2.5 w-2.5" /> En jeu
              </span>
            )}
          </div>
          <div className="flex items-baseline gap-1.5">
            <span className="text-3xl font-extrabold tabular-nums text-ink" data-testid="collab-dash-streak-current">{data.current_streak}</span>
            <span className="text-sm font-bold text-muted-ink">jour{data.current_streak > 1 ? "s" : ""} d'affilée</span>
          </div>
          <div className={`mt-0.5 text-[11px] ${data.streak_at_risk ? "font-semibold text-[#B45309]" : "text-muted-ink"}`}>
            {data.streak_at_risk
              ? "Aucun bulletin aujourd'hui — faites-en 1 pour ne pas perdre votre série !"
              : (data.current_streak > 0 ? "Continuez demain pour l'allonger !" : "Faites un bulletin aujourd'hui pour relancer votre série.")}
            {" "}Record : <b className="text-ink tabular-nums">{data.best_streak} j</b>
          </div>
        </div>
      </div>

      {/* MOIS EN COURS — objectif + message motivant */}
      <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-4 shadow-[var(--shadow-sm)]" data-testid="collab-dash-month">
        <div className="flex items-end justify-between gap-2">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-wide text-muted-ink">Bulletins ce mois</div>
            <div className="text-4xl font-extrabold tabular-nums text-[var(--shariti-orange)]" data-testid="collab-dash-month-count">{data.bulletins_mois || 0}</div>
          </div>
          <div className="text-right text-[11px] text-muted-ink">
            <div className="font-semibold">{data.objectif_label}</div>
            <div>Objectif : <b className="text-ink tabular-nums">{data.objectif_mois}</b></div>
          </div>
        </div>
        <div className="mt-2 h-2.5 overflow-hidden rounded-full bg-[var(--surface-2)]">
          <div className="h-full rounded-full bg-[var(--shariti-orange)] transition-[width] duration-500" style={{ width: `${objPct}%` }} data-testid="collab-dash-obj-bar" />
        </div>
        <div className="mt-2 flex items-start gap-1.5 rounded-lg bg-[rgba(238,114,46,0.06)] px-2.5 py-1.5 text-[12px] text-ink" data-testid="collab-dash-message">
          <Target className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[var(--shariti-orange)]" />
          <span>{data.message}</span>
        </div>
      </div>

      {/* SALARIÉS : RATIO DE PRODUCTIVITÉ (BS/h) par période, coloré selon le barème.
          Fond vert/orange/rouge = performance vs objectif (porte-à-porte ou site privé). */}
      {data.is_salarie && data.ratios && (
        <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-4 shadow-[var(--shadow-sm)]" data-testid="collab-dash-ratios">
          <div className="mb-1 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-muted-ink">
            <Gauge className="h-3.5 w-3.5 text-[var(--shariti-orange)]" /> Ratio de productivité (BS/h)
          </div>
          <p className="mb-3 text-[11px] text-muted-ink">Bulletins ÷ heures. Le fond indique si l'objectif est atteint, en temps réel.</p>
          <div className="grid grid-cols-2 gap-2.5">
            {[["jour", "Aujourd'hui"], ["semaine", "Cette semaine"], ["mois", "Ce mois"], ["annee", "Cette année"]].map(([k, label]) => {
              const r = data.ratios[k] || {};
              const reliable = !!r.fiable;                         // heures couvrent assez les bulletins
              const incomplete = !reliable && (r.bulletins || 0) > 0; // bulletins sans heures suffisantes
              const t = reliable ? zoneTone(r.zone) : ZONE_TONE.none;
              return (
                <div key={k} className={`rounded-xl border p-3 ${t.bg} ${t.border}`} data-testid={`ratio-${k}`} data-zone={reliable ? (r.zone || "na") : "na"} data-reliable={reliable ? "1" : "0"}>
                  <div className="flex items-center justify-between gap-1">
                    <span className="text-[10px] font-bold uppercase tracking-wide text-muted-ink">{label}</span>
                    <span className="shrink-0 rounded-full bg-black/5 px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wide text-muted-ink" data-testid={`ratio-kind-${k}`}>{kindLabel(r.kind)}</span>
                  </div>
                  {reliable ? (
                    <>
                      <div className={`mt-1 text-2xl font-extrabold tabular-nums ${t.text}`} data-testid={`ratio-bsh-${k}`}>{r.bs_h == null ? "—" : fmtH(r.bs_h)}</div>
                      <div className="text-[10px] text-muted-ink"><b className="text-ink tabular-nums">{r.bulletins_ratio ?? r.bulletins ?? 0}</b> BS · <span className="tabular-nums">{fmtH(r.heures)}</span> h</div>
                      {(r.bulletins_ratio != null && r.bulletins != null && r.bulletins_ratio < r.bulletins) && (
                        <div className="text-[9px] leading-tight text-muted-ink" data-testid={`ratio-hint-${k}`}>sur jours pointés · {r.bulletins} au total</div>
                      )}
                    </>
                  ) : incomplete ? (
                    <>
                      <div className="mt-1 flex items-baseline gap-1" data-testid={`ratio-bsh-${k}`}>
                        <span className="text-2xl font-extrabold tabular-nums text-ink">{(r.bulletins || 0).toLocaleString("fr-FR")}</span>
                        <span className="text-[11px] font-semibold text-muted-ink">BS</span>
                      </div>
                      <div className="mt-0.5 flex items-start gap-1 text-[10px] font-semibold leading-tight text-muted-ink" data-testid={`ratio-na-${k}`}>
                        <Info className="mt-px h-3 w-3 shrink-0" /> Ratio indisponible · heures incomplètes
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="mt-1 text-2xl font-extrabold tabular-nums text-muted-ink" data-testid={`ratio-bsh-${k}`}>—</div>
                      <div className="text-[10px] text-muted-ink"><b className="text-ink tabular-nums">{r.bulletins || 0}</b> BS · <span className="tabular-nums">{fmtH(r.heures)}</span> h</div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
          <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-muted-ink" data-testid="ratio-legend">
            <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm bg-emerald-500" />Objectif atteint</span>
            <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm bg-amber-500" />À améliorer</span>
            <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm bg-rose-500" />Sous l'objectif</span>
          </div>
          <p className="mt-2 flex items-start gap-1 text-[10px] leading-snug text-muted-ink" data-testid="ratio-note">
            <Info className="mt-px h-3 w-3 shrink-0" />
            <span>Le ratio n'apparaît que sur les périodes où vos heures couvrent vos bulletins. « Cette année » reste indisponible tant que le pointage des heures n'a pas couvert toute l'année (sinon le ratio serait faussé).</span>
          </p>
        </div>
      )}

      {/* BULLETINS MOIS PAR MOIS — visibilité complète de la production */}
      {(data.months_history || []).length > 0 && (() => {
        const maxM = Math.max(1, ...data.months_history.map((m) => m.n));
        return (
          <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-4 shadow-[var(--shadow-sm)]" data-testid="collab-dash-months">
            <div className="mb-3 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-muted-ink">
              <CalendarRange className="h-3.5 w-3.5 text-[var(--shariti-orange)]" /> Bulletins mois par mois
            </div>
            <div className="max-h-64 space-y-1.5 overflow-y-auto pr-1">
              {data.months_history.map((m) => (
                <div key={m.month} className="flex items-center gap-2" data-testid={`month-row-${m.month}`} data-zone={m.zone || "na"}
                  title={`${m.n} bulletin(s)${m.bs_h != null ? ` · ${fmtH(m.bs_h)} BS/h (${kindLabel(m.kind)})` : ""}`}>
                  <span className="w-[68px] shrink-0 text-[11px] font-semibold text-ink">{frMonth(m.month)}</span>
                  <div className="h-4 flex-1 overflow-hidden rounded bg-[var(--surface-2)]">
                    <div className={`h-full rounded ${zoneBar(m.zone)} transition-[width] duration-500`} style={{ width: `${Math.round((100 * m.n) / maxM)}%` }} />
                  </div>
                  <span className={`w-8 shrink-0 text-right text-[12px] font-extrabold tabular-nums ${m.zone ? zoneTone(m.zone).text : "text-ink"}`}>{m.n}</span>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* RECORDS */}
      <div className="grid grid-cols-3 gap-3" data-testid="collab-dash-records">
        <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-3 text-center shadow-[var(--shadow-sm)]">
          <Trophy className="mx-auto h-4 w-4 text-[#B98900]" />
          <div className="mt-1 text-lg font-extrabold tabular-nums text-ink" data-testid="collab-dash-best-day">{data.meilleur_jour ? data.meilleur_jour.n : 0}</div>
          <div className="text-[9px] font-semibold uppercase tracking-wide text-muted-ink">Meilleur jour</div>
        </div>
        <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-3 text-center shadow-[var(--shadow-sm)]">
          <CalendarDays className="mx-auto h-4 w-4 text-[var(--shariti-orange)]" />
          <div className="mt-1 text-lg font-extrabold tabular-nums text-ink" data-testid="collab-dash-active-days">{data.jours_actifs_mois || 0}</div>
          <div className="text-[9px] font-semibold uppercase tracking-wide text-muted-ink">Jours actifs</div>
        </div>
        <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-3 text-center shadow-[var(--shadow-sm)]">
          <Medal className="mx-auto h-4 w-4 text-[#5D3FD3]" />
          <div className="mt-1 text-lg font-extrabold tabular-nums text-ink" data-testid="collab-dash-record-month">{data.record_mois || 0}</div>
          <div className="text-[9px] font-semibold uppercase tracking-wide text-muted-ink">Record mensuel</div>
        </div>
      </div>

      {/* BADGES À COLLECTIONNER — débloqués selon la performance */}
      <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-4 shadow-[var(--shadow-sm)]" data-testid="collab-dash-badges">
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-muted-ink"><Award className="h-3.5 w-3.5 text-[var(--shariti-orange)]" /> Badges</div>
          <span className="rounded-full bg-[var(--surface-2)] px-2 py-0.5 text-[11px] font-bold tabular-nums text-ink" data-testid="collab-dash-badges-count">{data.badges_unlocked}/{data.badges_total}</span>
        </div>
        <div className="grid grid-cols-3 gap-2.5">
          {(data.badges || []).map((b) => {
            const Icon = BADGE_ICONS[b.icon] || Award;
            const isFresh = freshIds.includes(b.id);
            return (
              <div key={b.id} title={b.desc}
                className={`relative flex flex-col items-center gap-1 rounded-xl border p-2.5 text-center ${b.unlocked ? "border-[rgba(238,114,46,0.35)] bg-[rgba(238,114,46,0.06)]" : "border-[var(--border-soft)] bg-[var(--surface-2)]"}`}
                data-testid={`collab-dash-badge-${b.id}`} data-unlocked={b.unlocked}>
                {isFresh && (
                  <span className="absolute -right-1.5 -top-1.5 rounded-full bg-[var(--shariti-orange)] px-1.5 py-0.5 text-[7px] font-extrabold uppercase tracking-wide text-white shadow-[var(--shadow-sm)]" data-testid={`collab-dash-badge-new-${b.id}`}>Nouveau</span>
                )}
                <div className={`grid h-10 w-10 place-items-center rounded-full ${b.unlocked ? "bg-[var(--shariti-orange)] text-white" : "bg-[rgba(24,24,24,0.06)] text-muted-ink"}`}>
                  {b.unlocked ? <Icon className="h-5 w-5" /> : <Lock className="h-4 w-4" />}
                </div>
                <span className={`text-[10px] font-bold leading-tight ${b.unlocked ? "text-ink" : "text-muted-ink"}`}>{b.label}</span>
                <span className="text-[8px] leading-tight text-muted-ink">{b.desc}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* TENDANCE — 8 dernières semaines */}
      <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-4 shadow-[var(--shadow-sm)]" data-testid="collab-dash-trend">
        <div className="mb-3 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-muted-ink"><BarChart3 className="h-3.5 w-3.5 text-[var(--shariti-orange)]" /> 8 dernières semaines</div>
        <div className="flex items-end justify-between gap-1.5" style={{ height: 96 }}>
          {(data.weeks || []).map((w, i) => {
            const h = Math.round((w.n / maxWeek) * 74) + 4;
            const last = i === ((data.weeks || []).length - 1);
            return (
              <div key={w.start} className="flex flex-1 flex-col items-center justify-end gap-1" data-testid={`collab-dash-week-${i}`}>
                <span className="text-[10px] font-bold tabular-nums text-ink">{w.n}</span>
                <div className={`w-full rounded-t-md ${last ? "bg-[var(--shariti-orange)]" : "bg-[rgba(238,114,46,0.35)]"}`} style={{ height: h }} />
                <span className="text-[8px] text-muted-ink">{w.label}</span>
              </div>
            );
          })}
        </div>
      </div>

      <p className="text-center text-[11px] leading-relaxed text-muted-ink" data-testid="collab-dash-legal">
        {data.is_salarie
          ? "Bulletins comptés (hors invalides/annulés) et heures retenues — mêmes chiffres que ceux de l'administration."
          : "Volume de bulletins comptés (hors invalides/annulés). En tant qu'indépendant, vous n'êtes pas soumis au pointage des heures."}
      </p>
    </div>
  );
}
